import React, { FC, useCallback, useEffect, useRef } from 'react'
import cn from 'classnames'
import { useGoogleLogin } from '@lib/hooks/useGoogleLogin'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import s from './GoogleAuthView.module.scss'

export type LoginArguments = {
    credential: string
    websiteId: number
}

type GoogleCredentials = {
    clientId: string
    client_id: string
    credential: string
    select_by: string
}

const GOOGLE_CLIENT_ID =
    process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
    '1096615179983-un51f1pi7pvjbb60j6p45qvdj7tmrd85.apps.googleusercontent.com'

type Props = {
    closeAuthModal?: () => void
    updateCart?: any
    compact?: boolean
    buttonShape?: 'rectangular' | 'pill'
}

export const GoogleAuthView: FC<Props> = ({
    closeAuthModal,
    updateCart,
    compact = false,
    buttonShape = 'rectangular'
}) => {
    const { googleLogin, isLoading } = useGoogleLogin(updateCart)
    const { openToast } = useUI()

    const handleGoogleLogin = useCallback(
        async (data: GoogleCredentials) => {
            try {
                await googleLogin({
                    credential: data.credential,
                    websiteId: 1
                })
                closeAuthModal?.()
                openToast('Ingreso exitoso!', ToastType.Success, true)
            } catch (errors) {
                console.log('error:', errors)
            }
        },
        [googleLogin, closeAuthModal, openToast]
    )

    const handleGoogleLoginRef = useRef(handleGoogleLogin)
    handleGoogleLoginRef.current = handleGoogleLogin

    const signInContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        let cancelled = false
        let attempts = 0
        let layoutAttempts = 0
        const maxAttempts = 120
        const maxLayoutAttempts = 80

        let renderedWidth = 0

        const initGsiButton = () => {
            if (cancelled) return

            const g =
                (typeof window !== 'undefined' && (window as any).google) ||
                null
            const container = signInContainerRef.current

            if (!g?.accounts?.id || !container) {
                if (attempts++ < maxAttempts) {
                    window.setTimeout(initGsiButton, 50)
                }
                return
            }

            // El ancho usable es el content box: clientWidth incluye el padding
            // y, si el botón lo usa, el borde derecho queda fuera y se corta.
            const box = getComputedStyle(container)
            const padX =
                (parseFloat(box.paddingLeft) || 0) +
                (parseFloat(box.paddingRight) || 0)
            const raw =
                (container.clientWidth
                    ? container.clientWidth - padX
                    : 0) ||
                container.offsetWidth ||
                container.getBoundingClientRect().width ||
                container.parentElement?.clientWidth ||
                container.parentElement?.offsetWidth ||
                0
            if (raw < 100) {
                if (layoutAttempts++ < maxLayoutAttempts) {
                    window.setTimeout(initGsiButton, 50)
                }
                return
            }
            const maxBtn = compact ? 280 : 400
            const minBtn = compact ? 180 : 200
            const available = Math.floor(raw)
            // Por debajo del mínimo el texto no entra y GSI no dibuja el botón.
            if (available < minBtn) {
                if (layoutAttempts++ < maxLayoutAttempts) {
                    window.setTimeout(initGsiButton, 50)
                }
                return
            }
            const buttonWidth = Math.min(maxBtn, available)
            if (
                buttonWidth === renderedWidth &&
                container.querySelector('iframe')
            ) {
                return
            }

            /* Vaciar #signInDiv antes de renderizar (sin nodos/residuos de instancias GSI previas). */
            container.innerHTML = ''
            renderedWidth = buttonWidth

            g.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: (data: GoogleCredentials) => {
                    void handleGoogleLoginRef.current(data)
                },
                auto_select: false,
                itp_support: true,
                // Sin FedCM el botón queda en una línea ("Continuar con Google")
                // y no muestra "Continuar como…" ni el mail.
                use_fedcm_for_prompt: false,
                use_fedcm_for_button: false,
                button_auto_select: false
            })

            g.accounts.id.renderButton(container, {
                theme: 'outline',
                size: 'medium',
                type: 'standard',
                text: 'continue_with',
                shape: buttonShape,
                logo_alignment: 'left',
                width: buttonWidth
            })
        }

        initGsiButton()

        const observed = signInContainerRef.current
        const observer =
            observed && typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => initGsiButton())
                : null
        if (observed && observer) observer.observe(observed)

        return () => {
            cancelled = true
            observer?.disconnect()
            const el = signInContainerRef.current
            if (el) el.innerHTML = ''
        }
    }, [compact, buttonShape])

    const containerClass = isLoading
        ? s['signIn-container']
        : s['signIn-container-empty']

    return (
        <div className={cn(s['googleAuth-container'], compact && s['compact'])}>
            <div
                id="signInDiv"
                ref={signInContainerRef}
                className={containerClass}
            />

            {isLoading && (
                <div className={s['spinner-container']}>
                    <div className={s['spinner-showbox']}>
                        <div className={s['spinner-main']}>
                            <svg
                                className={s['spinner-circular']}
                                viewBox="25 25 50 50"
                            >
                                <circle
                                    className={s['spinner-path']}
                                    cx="50"
                                    cy="50"
                                    r="20"
                                    fill="none"
                                    strokeWidth="2"
                                    strokeMiterlimit="10"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
